import React, { useState } from 'react';
import { ExternalLink } from 'lucide-react';
import { cn } from '@cotera/client/v0/lib/utils';
import { Input } from '@cotera/client/v0/components/ui/input';
import { Button } from '@cotera/client/v0/components/ui/button';
import { Label } from '@cotera/client/v0/components/ui/label';
import { Loading } from '@cotera/client/app/components';
import type { CredentialFormProps, CredentialField } from './types';

// Terms that should be uppercased in credential field labels
const CREDENTIAL_TERM_CASING: Record<string, string> = {
  api: 'API',
  url: 'URL',
  id: 'ID',
  oauth: 'OAuth',
  jwt: 'JWT',
};

/**
 * Apply proper casing to credential field text.
 */
function formatCredentialText(text: string): string {
  let result = text;
  for (const [term, replacement] of Object.entries(CREDENTIAL_TERM_CASING)) {
    const regex = new RegExp(`\\b${term}\\b`, 'gi');
    result = result.replace(regex, replacement);
  }
  return result;
}

/**
 * Reusable credential entry form component.
 * This is a "dumb" component that receives all data as props.
 * Use it for tool authentication forms in both the modal and future chat contexts.
 */
export const CredentialForm: React.FC<CredentialFormProps> = ({
  provider: _provider,
  strategyName: _strategyName,
  fields,
  docsUrl,
  onSubmit,
  onChange,
  variant = 'default',
  submitLabel = 'Connect',
  showDocsLink = true,
  className,
  inputClassName,
}) => {
  const [saving, setSaving] = useState(false);
  const [credentials, setCredentials] = useState<Record<string, string>>(() => {
    const initial: Record<string, string> = {};
    for (const field of fields) {
      initial[field.name] = '';
    }
    return initial;
  });
  const [error, setError] = useState<string | null>(null);

  const handleCredentialsChange = (name: string, value: string) => {
    const newCredentials = { ...credentials, [name]: value };
    setCredentials(newCredentials);

    // Notify parent of current form values
    if (onChange) {
      onChange(newCredentials);
    }
  };

  const handleSubmit = async () => {
    if (!onSubmit) {
      return;
    }
    setSaving(true);
    setError(null);

    const missingFields = fields
      .filter((field) => field.required && !credentials[field.name])
      .map((field) => field.label);

    if (missingFields.length > 0) {
      setError(`Missing required fields: ${missingFields.join(', ')}`);
      setSaving(false);
      return;
    }

    try {
      await onSubmit(credentials);
    } catch (err) {
      const message =
        err instanceof Error ? err.message : 'Failed to save credentials';
      setError(message);
    } finally {
      setSaving(false);
    }
  };

  const lastFieldIndex = fields.length - 1;
  const isCompact = variant === 'compact';

  const showSubmitButton = onSubmit !== undefined;

  return (
    <div
      className={cn('flex flex-col', isCompact ? 'gap-3' : 'gap-4', className)}
    >
      {fields.map((field, index) => (
        <CredentialFieldInput
          key={field.name}
          field={field}
          value={credentials[field.name] ?? ''}
          onChange={(value) => handleCredentialsChange(field.name, value)}
          isLastField={index === lastFieldIndex}
          saving={saving}
          submitLabel={submitLabel}
          docsUrl={docsUrl}
          showHelpLink={showDocsLink && index === 0 && docsUrl !== null}
          isCompact={isCompact}
          inputClassName={inputClassName}
          showSubmitButton={showSubmitButton}
          onSubmit={handleSubmit}
        />
      ))}

      {error !== null && (
        <div className="text-destructive text-sm bg-destructive/10 px-3 py-2 rounded-md border border-destructive/20">
          {error}
        </div>
      )}
    </div>
  );
};

/**
 * Individual credential field input with label and optional submit button.
 */
const CredentialFieldInput: React.FC<{
  field: CredentialField;
  value: string;
  onChange: (value: string) => void;
  isLastField: boolean;
  saving: boolean;
  submitLabel: string;
  docsUrl?: string | null;
  showHelpLink: boolean;
  isCompact: boolean;
  inputClassName?: string;
  showSubmitButton?: boolean;
  onSubmit?: () => void | Promise<void>;
}> = ({
  field,
  value,
  onChange,
  isLastField,
  saving,
  submitLabel,
  docsUrl,
  showHelpLink,
  isCompact,
  inputClassName,
  showSubmitButton,
  onSubmit,
}) => {
  const isTextarea = field.type === 'textarea';

  return (
    <div className={cn('flex flex-col', isCompact ? 'gap-1.5' : 'gap-2')}>
      <div
        className={cn(
          'flex items-center justify-between',
          showHelpLink && isLastField && 'pr-[108px]'
        )}
      >
        <Label
          htmlFor={field.name}
          className={cn(
            'font-medium text-foreground/90',
            isCompact ? 'text-xs' : 'text-sm'
          )}
        >
          {formatCredentialText(field.label)}
          {field.required && <span className="text-red-500 ml-1">*</span>}
        </Label>
        {showHelpLink && docsUrl !== null && (
          <a
            href={`${docsUrl}#authentication`}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground transition-colors"
          >
            <ExternalLink className="h-3 w-3" />
            <span>Need help?</span>
          </a>
        )}
      </div>

      {isTextarea ? (
        <div className="flex flex-col gap-3">
          <textarea
            id={field.name}
            className={cn(
              'flex w-full rounded-md border border-input bg-transparent px-3 py-2 text-base shadow-xs ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-ring/50 focus-visible:border-ring disabled:cursor-not-allowed disabled:opacity-50 transition-[color,box-shadow]',
              isCompact ? 'min-h-[100px]' : 'min-h-[140px]',
              inputClassName
            )}
            placeholder={formatCredentialText(field.placeholder ?? '')}
            value={value}
            onChange={(e) => onChange(e.target.value)}
            onKeyDown={(e) => {
              // Prevent enter key from triggering parent form submission
              if (e.key === 'Enter') {
                e.preventDefault();
                e.stopPropagation();
              }
            }}
          />
          {isLastField && showSubmitButton && (
            <div className="flex justify-end">
              <Button type="button" onClick={onSubmit} disabled={saving}>
                {saving ? <Loading.Spinner variant="sm" /> : submitLabel}
              </Button>
            </div>
          )}
        </div>
      ) : (
        <div className={cn('flex gap-3', isLastField ? 'items-center' : '')}>
          <Input
            id={field.name}
            type={field.type}
            placeholder={formatCredentialText(field.placeholder ?? '')}
            value={value}
            className={cn('flex-1', isCompact ? 'h-9' : 'h-10', inputClassName)}
            onChange={(e) => onChange(e.target.value)}
            onKeyDown={(e) => {
              // Prevent enter key from triggering parent form submission
              if (e.key === 'Enter') {
                e.preventDefault();
                e.stopPropagation();
              }
            }}
          />
          {isLastField && showSubmitButton && (
            <Button
              type="button"
              onClick={onSubmit}
              disabled={saving}
              className="shrink-0"
            >
              {saving ? <Loading.Spinner variant="sm" /> : submitLabel}
            </Button>
          )}
        </div>
      )}

      {field.helpText !== undefined && field.helpText.length > 0 && (
        <p
          className={cn(
            'text-muted-foreground',
            isCompact ? 'text-[11px]' : 'text-xs'
          )}
        >
          {field.helpText}
        </p>
      )}
    </div>
  );
};
